import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { useEntityActivation, useEntityDetail, useEntityRelationLifecycle } from "~/features/entities/hooks";
import type { EntityDetailTab } from "~/features/entities/types";
import { MAX_CASCADE_RELATION_IDS } from "~/features/entities/types";
import {
  toEntityChangeRequestRows,
  toEntityDetailUI,
  toEntityEvidenceRows,
  toEntityLineage,
} from "~/features/entities/utils/transformers";
import { useEffectiveRecordPropertySchemas } from "~/features/recordDetails/hooks/useEffectiveRecordPropertySchemas";
import { v2Route } from "~/v2/routes";
import { EntityActivationDialog } from "./EntityActivationDialog";
import { EntityDetailPresenter } from "./EntityDetailPresenter";

interface EntityDetailProps {
  id: string;
  onBack: () => void;
  variant?: "page" | "peek";
}

export function EntityDetail({ id, onBack, variant = "page" }: EntityDetailProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const navigate = useNavigate();
  const [params, setParams] = useTypedSearchParams(v2Route.entities);
  const { data, refetch: refetchDetail, recordGeoItems } = useEntityDetail(id);
  const { onActivate, onDeactivate, processing } = useEntityActivation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { relations, loading: relationsLoading, error: relationsError } = useEntityRelationLifecycle(id, confirmOpen);
  const [selectedRelationIds, setSelectedRelationIds] = useState<string[]>([]);

  const activeTab: EntityDetailTab = params["tab-entityDetail"] ?? "data";
  const geoItemId = params.geoItemId ?? undefined;
  const onTabChange = useCallback(
    (next: EntityDetailTab) => {
      setParams((prev) => ({ ...prev, "tab-entityDetail": next === "data" ? undefined : next }));
    },
    [setParams],
  );

  const raw = data.records[0];
  const { schemas: effectiveSchemas, refetch: refetchEffectiveSchemas } = useEffectiveRecordPropertySchemas(
    raw?.recordMetadata.recordMetadataCategories.map(({ category }) => category.id) ?? [],
  );
  const relatedCompoundEntity = data.relatedCompoundEntities[0];
  const refetch = useCallback(async () => {
    const updated = await refetchDetail();
    const categoryIDs =
      updated.data?.records[0]?.recordMetadata.recordMetadataCategories.map(({ category }) => category.id) ?? [];
    await refetchEffectiveSchemas(categoryIDs);
    return updated;
  }, [refetchDetail, refetchEffectiveSchemas]);
  const detail = useMemo(
    () => (raw ? toEntityDetailUI(raw, relatedCompoundEntity, locale, recordGeoItems, effectiveSchemas) : null),
    [raw, relatedCompoundEntity, locale, recordGeoItems, effectiveSchemas],
  );
  const changeRequestRows = useMemo(() => (raw ? toEntityChangeRequestRows(raw) : []), [raw]);
  const evidenceRows = useMemo(() => (raw ? toEntityEvidenceRows(raw) : []), [raw]);
  const lineage = useMemo(
    () =>
      raw
        ? toEntityLineage(raw, {
            record: detail?.displayName || raw.id,
            changeRequest: t("model.changeRequest.modelName"),
          })
        : { nodes: [], edges: [] },
    [raw, detail, t],
  );

  const handleCopyId = useCallback(
    async (value: string) => {
      try {
        await copyToClipboard(value);
        toast.success(t("feature.common.idCopied"));
      } catch {
        toast.error(t("feature.common.copyToClipboardFailed"));
      }
    },
    [t],
  );

  // Everything activatable starts ticked, up to the cap the approval will accept: an operator
  // reactivating an entity usually wants its relations back, and unticking is the exception.
  // Re-seeded whenever the offer changes, which is when the dialog opens and its query lands.
  useEffect(() => {
    setSelectedRelationIds(
      relations.activatable.slice(0, MAX_CASCADE_RELATION_IDS).map((relation) => relation.relationId),
    );
  }, [relations.activatable]);

  const handleToggleRelation = useCallback((relationId: string, next: boolean) => {
    setSelectedRelationIds((prev) => {
      if (!next) return prev.filter((id) => id !== relationId);
      if (prev.includes(relationId)) return prev;
      // Silently dropping the extra one would be worse than refusing to tick it: the dialog
      // would say one thing and the request would carry another.
      if (prev.length >= MAX_CASCADE_RELATION_IDS) return prev;
      return [...prev, relationId];
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!raw) return;
    const target = { id: raw.id, recordMetadataId: raw.recordMetadata.id };
    const ok = await (raw.deactivatedAt ? onActivate(target, selectedRelationIds) : onDeactivate(target));
    setConfirmOpen(false);
    if (ok) void refetch();
  }, [raw, onActivate, onDeactivate, refetch, selectedRelationIds]);

  if (!raw || !detail) {
    return <div className="mx-auto max-w-[var(--content-max)] p-8 text-[13px] text-fg-3">{t("ui.notFound")}</div>;
  }

  return (
    <>
      <EntityDetailPresenter
        detail={detail}
        onBack={onBack}
        onCopyId={handleCopyId}
        onRequestToggleActivation={() => setConfirmOpen(true)}
        onSelectEntity={(entityId) => navigate(`/entities/${entityId}`)}
        activeTab={activeTab}
        onTabChange={onTabChange}
        geoItemId={geoItemId}
        changeRequests={changeRequestRows}
        evidences={evidenceRows}
        lineage={lineage}
        variant={variant}
      />
      <EntityActivationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        deactivate={!detail.isDeactivated}
        processing={processing}
        relations={relations}
        relationsLoading={relationsLoading}
        relationsFailed={relationsError != null}
        selectedRelationIds={selectedRelationIds}
        onToggleRelation={handleToggleRelation}
        onConfirm={handleConfirm}
      />
    </>
  );
}
